import React, { useEffect, useState } from 'react';
import api from '../api';
import { Plus, Trash2, Save, RefreshCw, Shield, Network, AlertTriangle, CheckCircle2, X } from 'lucide-react';

export default function DnsRecursor() {
  const [acls, setAcls] = useState([]);
  const [form, setForm] = useState({ cidr: '', description: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg]   = useState(null);
  const [me, setMe]     = useState(null);

  const load = () => api.get('/dns/recursion').then(r => setAcls(r.data));
  useEffect(() => { load(); api.get('/me').then(r => setMe(r.data.user)).catch(()=>{}); }, []);

  const isAdmin = me?.role === 'admin' || me?.canAll;

  const toast = (kind, text) => { setMsg({kind, text}); setTimeout(()=>setMsg(null), 5000); };

  const add = async () => {
    if (!form.cidr) return;
    setBusy(true);
    try {
      await api.post('/dns/recursion', form);
      setForm({ cidr: '', description: '' });
      load();
      toast('ok', '✓ CIDR adicionado e BIND9 reconfigurado');
    } catch (e) {
      toast('err', '✗ ' + (e.response?.data?.error || 'Erro'));
    } finally { setBusy(false); }
  };

  const toggle = async (a) => {
    setBusy(true);
    try {
      await api.put(`/dns/recursion/${a.id}`, { enabled: !a.enabled });
      load();
    } catch (e) { toast('err', e.response?.data?.error || 'Erro'); }
    finally { setBusy(false); }
  };

  const del = async (a) => {
    if (!window.confirm(`Remover ${a.cidr} da ACL? Clientes nessa faixa não conseguirão mais recursão.`)) return;
    setBusy(true);
    try {
      await api.delete(`/dns/recursion/${a.id}`);
      load();
      toast('ok', '✓ Removido');
    } catch (e) { toast('err', e.response?.data?.error || 'Erro'); }
    finally { setBusy(false); }
  };

  const reapply = async () => {
    setBusy(true);
    try {
      const r = await api.post('/dns/recursion/reapply');
      toast('ok', '✓ ACL reaplicada · ' + (r.data.output || '').split('\n').filter(Boolean).pop());
    } catch (e) { toast('err', e.response?.data?.error || 'Erro'); }
    finally { setBusy(false); }
  };

  const enabledCount = acls.filter(a => a.enabled).length;

  return (
    <div className="stack-lg">
      {msg && (
        <div style={{
          position:'fixed', top:16, right:16, zIndex:9999, maxWidth:480,
          padding:'12px 16px', borderRadius:'var(--radius)',
          background: msg.kind === 'ok' ? 'var(--ok-dim)' : 'var(--err-dim)',
          border: '1px solid ' + (msg.kind === 'ok' ? 'rgba(63,185,80,.3)' : 'rgba(248,81,73,.3)'),
          color: msg.kind === 'ok' ? 'var(--ok)' : 'var(--err)',
          fontSize: 13, fontWeight: 500,
        }}>{msg.text}</div>
      )}

      <header className="row-between">
        <div>
          <h1 className="h1">Resolvedor DNS</h1>
          <p className="dim" style={{ fontSize: 14, marginTop: 4 }}>
            Controla quem pode usar o BIND9 (ns1/ns2) como <strong>resolvedor recursivo</strong> de DNS.
            O serviço autoritativo (resposta às suas zonas) continua aberto a todo mundo.
          </p>
        </div>
        {isAdmin && (
          <button className="btn btn-secondary" onClick={reapply} disabled={busy}>
            <RefreshCw size={13}/> Reaplicar ACL
          </button>
        )}
      </header>

      {/* Aviso de segurança */}
      <div className="card" style={{borderColor:'rgba(0,212,255,0.3)', background:'linear-gradient(135deg, rgba(0,212,255,0.06), transparent 60%)'}}>
        <div className="hrow" style={{padding:'14px 18px', gap:14}}>
          <div style={{width:38, height:38, borderRadius:10, background:'var(--accent-dim)',
                       display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}>
            <Shield size={20} color="var(--accent)"/>
          </div>
          <div style={{flex:1}}>
            <div style={{fontSize:14, fontWeight:600}}>{enabledCount} faixa(s) com permissão de recursão</div>
            <div className="dim" style={{fontSize:12.5, marginTop:2}}>
              Apenas IPs dentro dessas redes podem usar <code>203.0.113.11</code>/<code>203.0.113.12</code>
              como servidor DNS (resolução de domínios externos). Fora delas, só zonas autoritativas são respondidas.
            </div>
          </div>
        </div>
      </div>

      {/* Lista */}
      <div className="card">
        <div className="card-header">
          <Network size={15} color="var(--accent)"/>
          <h3 className="h3">Faixas autorizadas</h3>
          <span className="badge muted">{acls.length} total</span>
        </div>
        {acls.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon"><AlertTriangle size={26}/></div>
            <div className="empty-title">Nenhuma faixa configurada</div>
            <div className="empty-desc">Sem ACLs, somente loopback consegue recursão.</div>
          </div>
        ) : (
          <table className="table">
            <thead><tr>
              <th style={{width:30}}></th>
              <th>CIDR</th>
              <th>Descrição</th>
              <th>Status</th>
              {isAdmin && <th style={{width:120}}></th>}
            </tr></thead>
            <tbody>
              {acls.map(a => (
                <tr key={a.id}>
                  <td><span className={'dot ' + (a.enabled ? 'ok' : 'err')}/></td>
                  <td className="mono" style={{fontWeight:600, fontSize:13}}>{a.cidr}</td>
                  <td className="dim" style={{fontSize:12.5}}>{a.description || '–'}</td>
                  <td>
                    <span className={'badge ' + (a.enabled ? 'ok' : 'muted')}>
                      {a.enabled ? 'Ativo' : 'Desabilitado'}
                    </span>
                  </td>
                  {isAdmin && (
                    <td style={{textAlign:'right'}}>
                      <button className="btn btn-ghost btn-sm" onClick={()=>toggle(a)} disabled={busy} title={a.enabled?'Desabilitar':'Habilitar'}>
                        {a.enabled ? <X size={12}/> : <CheckCircle2 size={12}/>}
                      </button>
                      <button className="btn btn-ghost btn-icon" onClick={()=>del(a)} disabled={busy} title="Remover">
                        <Trash2 size={12}/>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Form */}
      {isAdmin && (
        <div className="card">
          <div className="card-header">
            <Plus size={15} color="var(--accent)"/>
            <h3 className="h3">Adicionar faixa</h3>
          </div>
          <div className="card-body stack">
            <div className="grid-2">
              <div className="field">
                <label>CIDR (rede/prefixo)</label>
                <input className="input mono" value={form.cidr}
                  onChange={e=>setForm({...form, cidr:e.target.value})}
                  onKeyDown={e=>e.key==='Enter'&&add()}
                  placeholder="203.0.113.0/24 ou 2001:db8::/32"/>
              </div>
              <div className="field">
                <label>Descrição (opcional)</label>
                <input className="input" value={form.description}
                  onChange={e=>setForm({...form, description:e.target.value})}
                  onKeyDown={e=>e.key==='Enter'&&add()}
                  placeholder="Ex: clientes residenciais"/>
              </div>
            </div>
            <div>
              <button className="btn btn-primary" onClick={add} disabled={busy || !form.cidr}>
                <Save size={13}/> Adicionar e reaplicar
              </button>
            </div>
            <p className="dim" style={{fontSize:12, lineHeight:1.6}}>
              <strong>Aceita IPv4 e IPv6.</strong> Quando adicionado, o BIND9 é reconfigurado automaticamente.
              Exemplo de redes a liberar: seu bloco IPv4 (<code>203.0.113.0/24</code>), bloco IPv6 (<code>2001:db8::/32</code>),
              rede interna do escritório, etc.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
